import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { matchesQuery, snippet, statusText, type Task } from '@machs/shared';
import { useSync } from '../lib/sync';
import { Icon, type IconName } from '../lib/icons';
import { avatarGradient, initials } from '../lib/format';
import { imageUrl } from '../lib/images';
import { Avatar } from './ui';

// Globale Suche, überall mit Cmd-K bzw. Strg-K. Sucht offline im lokalen
// Bestand: Aufgaben, Nachrichten und ausgewertete Medien.

interface Hit {
  task: Task;
  text: string;
  icon: IconName;
  kind: 'aufgabe' | 'nachricht' | 'medium';
}

export function CommandSearch({ onOpenTask, onNewTask }: { onOpenTask: (id: string) => void; onNewTask: () => void }) {
  const { client, version } = useSync();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('machs:search', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('machs:search', onOpenEvent);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => input.current?.focus());
    }
  }, [open]);

  const hits = useMemo(() => {
    const q = query.trim();
    const out: Hit[] = [];
    const recent = [...client.data.task.values()]
      .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));
    // Ohne Eingabe die zuletzt genutzten Aufgaben zeigen
    if (q.length < 2) return recent.slice(0, 8).map((task) => ({ task, text: task.title, icon: 'chats' as IconName, kind: 'aufgabe' as const }));
    for (const t of recent) if (matchesQuery(t.title, q)) out.push({ task: t, text: t.title, icon: 'chats', kind: 'aufgabe' });
    for (const m of client.data.message.values()) {
      const task = client.data.task.get(m.taskId);
      if (!task || m.deletedAt) continue;
      if (m.body && matchesQuery(m.body, q)) out.push({ task, text: snippet(m.body, q), icon: 'text', kind: 'nachricht' });
      const media = m.mediaId ? client.data.media.get(m.mediaId) : null;
      if (media) {
        const content = [media.linkTitle, media.fileName, media.summary, media.points.map((x) => x.text).join(' '), media.transcript, media.imageText]
          .filter(Boolean)
          .join(' ');
        if (content && matchesQuery(content, q)) {
          out.push({ task, text: snippet(content, q), icon: media.kind === 'sprache' ? 'wave' : media.kind === 'bild' ? 'image' : 'video', kind: 'medium' });
        }
      }
    }
    return out.slice(0, 60);
  }, [client, version, query]);

  useEffect(() => setActive(0), [query]);

  if (!open) return null;

  function choose(i: number) {
    const hit = hits[i];
    if (!hit) return;
    onOpenTask(hit.task.id);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') return setOpen(false);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(Math.max(active + (e.key === 'ArrowDown' ? 1 : -1), 0), hits.length - 1);
      setActive(next);
      list.current?.children[next]?.scrollIntoView({ block: 'nearest' });
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      choose(active);
    }
  }

  return createPortal(
    <div className="overlay cmd-overlay" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
      <div className="cmd" role="dialog" aria-modal="true" aria-label="Suchen">
        <div className="cmd-input">
          <Icon name="search" size={18} className="flex-none text-[var(--muted)]" />
          <input
            ref={input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Aufgaben, Nachrichten, Videos und Sprache durchsuchen"
            aria-label="Suchbegriff"
          />
          <kbd>esc</kbd>
        </div>
        <div className="cmd-list scroll" ref={list}>
          {hits.length === 0 ? (
            <p className="px-4 py-8 text-center text-[var(--muted)]">Nichts gefunden.</p>
          ) : (
            hits.map((h, i) => {
              const project = client.data.project.get(h.task.projectId);
              return (
                <button
                  key={`${h.task.id}-${i}`}
                  className={`cmd-row ${i === active ? 'on' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(i)}
                >
                  <Avatar
                    name={initials(project?.name ?? h.task.title)}
                    gradient={project?.color ?? avatarGradient(project?.id ?? h.task.id)}
                    src={imageUrl(project?.imageMediaId)}
                    size={34}
                    square
                  />
                  <span className="min-w-0 flex-1 text-left">
                    <b className="block truncate text-[14.5px]">{h.task.title}</b>
                    <small className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-[var(--muted)]">
                      <Icon name={h.icon} size={13} className="flex-none" />
                      <span className="truncate">{h.kind === 'aufgabe' ? `${project?.name ?? 'Projekt'}, ${statusText(h.task.status)}` : h.text}</span>
                    </small>
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="cmd-foot">
          <button onClick={() => (setOpen(false), onNewTask())}><Icon name="plus" size={15} />Neue Aufgabe</button>
          <span className="ml-auto flex items-center gap-1"><kbd>↑</kbd><kbd>↓</kbd> wählen<kbd className="ml-2">↵</kbd> öffnen</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
